/**
 * Browser Tool Definitions
 *
 * Source-of-truth tool definitions as plain objects with JSON Schema.
 * Used by CLI for MCP tool registration. API references these for consistency
 * but keeps its own Zod schemas for the `ai` package.
 *
 * @module
 */

export interface BrowserToolDefinition {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
}

export function getBrowserToolDefinitions(): BrowserToolDefinition[] {
  return [
    {
      name: 'browser_navigate',
      description: 'Navigate to a URL in the browser',
      inputSchema: {
        type: 'object',
        properties: {
          url: { type: 'string', description: 'The URL to navigate to' },
        },
        required: ['url'],
      },
    },
    {
      name: 'browser_navigate_back',
      description: 'Navigate back to the previous page',
      inputSchema: {
        type: 'object',
        properties: {},
      },
    },
    {
      name: 'browser_snapshot',
      description:
        'Get a snapshot of the page accessibility tree. ' +
        'Use search="term" to find elements near your current focus. ' +
        'Search returns ONLY relevant matches within your working context ' +
        '(the dialog, form, or section containing the active element). ' +
        'Results are highlighted in frontmatter with coordinates and on screenshot.',
      inputSchema: {
        type: 'object',
        properties: {
          mode: {
            type: 'string',
            enum: ['auto', 'both', 'screenshot', 'tree'],
            description:
              'What to return: "auto" (live-snapshot default) = tree + screenshot only when useful, "both" = tree + screenshot, "screenshot" = screenshot only (use coordinate-based clicking), "tree" = tree only',
          },
          expand: {
            type: 'string',
            description: 'Ref of a collapsed section to expand and show full details',
          },
          search: {
            type: 'string',
            description:
              'Search for 1-2 specific terms to LOCATE elements for your next action. ' +
              'Terms are OR-matched (use "|"), case-insensitive, and ignore punctuation. ' +
              'Results filtered to your current working context (not the whole page). ' +
              'IMPORTANT: Search overlays can obscure subtle UI states. Use search to LOCATE elements, ' +
              'not to VALIDATE visual states. If search results are confusing, take a snapshot without search for a clearer view. ' +
              'Example: "Save" or "Email|Username"',
          },
          showCoordinateGrid: {
            type: 'boolean',
            description:
              'Overlay a coordinate grid showing pixel positions. ' +
              'Helps estimate click coordinates when using coordinate-based interaction.',
          },
          probeAt: {
            type: 'object',
            properties: {
              x: { type: 'number', description: 'X coordinate in viewport pixels' },
              y: { type: 'number', description: 'Y coordinate in viewport pixels' },
            },
            required: ['x', 'y'],
            description:
              'RECOMMENDED before any coordinate-based click. Probes what element is at a pixel position. ' +
              'Returns the full tree with a PROBE RESULT in frontmatter showing the ref, role, and label ' +
              'of the element at those coordinates. If the probe returns a ref, prefer browser_click ref=... ' +
              'over coordinate clicking. Mutually exclusive with search.',
          },
          hintDetail: {
            type: 'string',
            description:
              'Ref of an element with [+docs] marker to expand its full hint documentation. ' +
              'Returns the snapshot with the hint body appended.',
          },
        },
      },
    },
    {
      name: 'browser_screenshot',
      description:
        'Take a screenshot of the current page or a specific element. Optionally provide a human-readable label to reference this screenshot later.',
      inputSchema: {
        type: 'object',
        properties: {
          fullPage: {
            type: 'boolean',
            description: 'Whether to capture the full page or just viewport',
          },
          element: { type: 'string', description: 'Human-readable element description' },
          ref: { type: 'string', description: 'Exact element reference from snapshot' },
          label: {
            type: 'string',
            description: 'Human-readable label to reference this screenshot later',
          },
          returnImage: {
            type: 'boolean',
            description:
              'When true, returns the image data so the model can see it. Use sparingly as images consume many tokens.',
          },
        },
      },
    },
    {
      name: 'browser_evaluate',
      description: 'Execute JavaScript in the page context and return the result',
      inputSchema: {
        type: 'object',
        properties: {
          function: {
            type: 'string',
            description: 'JavaScript function to execute (e.g., "() => window.location.href")',
          },
          label: {
            type: 'string',
            description: 'Human-readable description of what this evaluate does',
          },
          ref: { type: 'string', description: 'Exact element reference to evaluate on' },
        },
        required: ['function', 'label'],
      },
    },
    {
      name: 'browser_console_messages',
      description: 'Get console messages from the page',
      inputSchema: {
        type: 'object',
        properties: {
          onlyErrors: { type: 'boolean', description: 'Only return error messages' },
        },
      },
    },
    {
      name: 'browser_network_requests',
      description: 'Get network requests made by the page',
      inputSchema: {
        type: 'object',
        properties: {},
      },
    },
    {
      name: 'browser_click',
      description:
        'Click an element on the page. Provide EITHER a ref from the snapshot OR coordinates (x, y).\n' +
        '- **ref**: Uses Playwright locator - handles scrolling into view and actionability checks automatically.\n' +
        '- **coordinates**: Clicks at exact pixel position - useful when ref-based clicking fails due to overlays.\n' +
        'Always describe what you are clicking in the element parameter.',
      inputSchema: {
        type: 'object',
        properties: {
          ref: {
            type: 'string',
            description: 'Element reference from snapshot (optional if x/y coordinates provided)',
          },
          x: { type: 'number', description: 'X coordinate on the page (use with y)' },
          y: { type: 'number', description: 'Y coordinate on the page (use with x)' },
          element: { type: 'string', description: 'Description of what you are clicking' },
          doubleClick: { type: 'boolean', description: 'Perform a double click' },
          button: {
            type: 'string',
            description: 'Mouse button to click: left, right, middle',
          },
          modifiers: {
            type: 'array',
            items: { type: 'string' },
            description: 'Keyboard modifiers: Alt, Control, Meta, Shift',
          },
        },
        required: ['element'],
      },
    },
    {
      name: 'browser_hover',
      description:
        'Hover over an element to reveal hidden content (dropdowns, tooltips, submenus). ' +
        'This does NOT click - use browser_click after hovering to interact with revealed items.',
      inputSchema: {
        type: 'object',
        properties: {
          ref: { type: 'string', description: 'The element reference from snapshot' },
          element: { type: 'string', description: 'Description of what you are hovering over' },
        },
        required: ['ref', 'element'],
      },
    },
    {
      name: 'browser_drag',
      description:
        'Drag between two points. Provide EITHER refs OR coordinates.\n' +
        '- **refs**: startRef + endRef — uses Playwright locators for drag-and-drop.\n' +
        '- **coordinates**: startX, startY, endX, endY — drags between exact pixel positions.',
      inputSchema: {
        type: 'object',
        properties: {
          startRef: { type: 'string', description: 'Starting element reference' },
          startElement: { type: 'string', description: 'Description of starting element' },
          endRef: { type: 'string', description: 'Target element reference' },
          endElement: { type: 'string', description: 'Description of target element' },
          startX: { type: 'number', description: 'Start X coordinate' },
          startY: { type: 'number', description: 'Start Y coordinate' },
          endX: { type: 'number', description: 'End X coordinate' },
          endY: { type: 'number', description: 'End Y coordinate' },
        },
        required: ['startElement', 'endElement'],
      },
    },
    {
      name: 'browser_type',
      description:
        'Type text into an input field and optionally submit the form. ' +
        'WARNING: For date inputs (type="date"), use ISO format (YYYY-MM-DD) with browser_fill_form instead.',
      inputSchema: {
        type: 'object',
        properties: {
          ref: { type: 'string', description: 'The element reference from snapshot' },
          text: { type: 'string', description: 'The text to type' },
          element: { type: 'string', description: 'Description of the input field' },
          submit: { type: 'boolean', description: 'Whether to submit the form after typing' },
          delay: { type: 'number', description: 'Delay between keystrokes in milliseconds' },
        },
        required: ['ref', 'text', 'element'],
      },
    },
    {
      name: 'browser_press_key',
      description: 'Press a keyboard key',
      inputSchema: {
        type: 'object',
        properties: {
          key: {
            type: 'string',
            description: 'Key to press (e.g., "Enter", "Escape", "ArrowDown", "Control+A")',
          },
        },
        required: ['key'],
      },
    },
    {
      name: 'browser_fill_form',
      description: 'Fill multiple form fields at once',
      inputSchema: {
        type: 'object',
        properties: {
          fields: {
            type: 'array',
            items: {
              type: 'object',
              properties: {
                ref: { type: 'string', description: 'Element reference' },
                element: { type: 'string', description: 'Element description' },
                value: { type: 'string', description: 'Value to fill' },
              },
              required: ['ref', 'value'],
            },
            description: 'Array of form fields to fill',
          },
        },
        required: ['fields'],
      },
    },
    {
      name: 'browser_select_option',
      description: 'Select an option from a dropdown',
      inputSchema: {
        type: 'object',
        properties: {
          ref: { type: 'string', description: 'Select element reference' },
          value: { type: 'string', description: 'Option value to select' },
          element: { type: 'string', description: 'Description of the dropdown' },
        },
        required: ['ref', 'value', 'element'],
      },
    },
    {
      name: 'browser_file_upload',
      description: 'Upload one or multiple files',
      inputSchema: {
        type: 'object',
        properties: {
          paths: {
            type: 'array',
            items: { type: 'string' },
            description: 'Array of file paths to upload',
          },
        },
        required: ['paths'],
      },
    },
    {
      name: 'browser_scroll',
      description:
        'Scroll the page. Returns a screenshot showing the new viewport. Three modes:\n' +
        '1. **Scroll at coordinates**: Use x + y to scroll at a specific point\n' +
        '2. **Scroll by amount**: Use direction + amount to scroll up/down/left/right\n' +
        '3. **Scroll to element**: Use toRef to scroll so that element is centered in the viewport',
      inputSchema: {
        type: 'object',
        properties: {
          direction: {
            type: 'string',
            enum: ['up', 'down', 'left', 'right'],
            description: 'Direction to scroll (ignored if toRef is provided)',
          },
          amount: {
            type: 'number',
            description: 'Scroll intensity from 1-10 (default 3). Ignored if toRef is provided.',
          },
          withinRef: {
            type: 'string',
            description: 'Scroll within this scrollable element instead of the page',
          },
          toRef: {
            type: 'string',
            description: 'Scroll the page so this element is centered in the viewport. Handles both vertical and horizontal scrolling automatically — works for grid columns that are horizontally off-screen. A ref marked [scroll-left]/[scroll-right] in the snapshot is clipped inside a horizontal scroll container; pass it here to bring it into view.',
          },
          x: {
            type: 'number',
            description: 'X coordinate for visual scroll origin.',
          },
          y: {
            type: 'number',
            description: 'Y coordinate for visual scroll origin.',
          },
        },
        required: ['direction'],
      },
    },
    {
      name: 'browser_handle_dialog',
      description:
        'Pre-register how to handle the next browser dialog (alert, confirm, prompt). ' +
        'Call this with action "accept" BEFORE re-clicking the button that triggered the dialog.',
      inputSchema: {
        type: 'object',
        properties: {
          action: {
            type: 'string',
            enum: ['accept', 'dismiss'],
            description: 'Action to take on the dialog',
          },
          promptText: { type: 'string', description: 'Text to enter in prompt dialog' },
        },
        required: ['action'],
      },
    },
    {
      name: 'browser_dismiss_overlay',
      description:
        'Systematically attempt to dismiss any modal, overlay, or dialog blocking the page. ' +
        'Tries multiple strategies: Escape key, close button, backdrop click.',
      inputSchema: {
        type: 'object',
        properties: {
          preferredStrategy: {
            type: 'string',
            enum: ['escape', 'close_button', 'backdrop', 'auto'],
            description: 'Preferred dismissal method. "auto" (default) tries all strategies.',
          },
        },
      },
    },
    {
      name: 'browser_wait_for',
      description: 'Wait for a condition or time period',
      inputSchema: {
        type: 'object',
        properties: {
          time: { type: 'number', description: 'Time to wait in seconds' },
          text: { type: 'string', description: 'Text to wait for to appear' },
          textGone: { type: 'string', description: 'Text to wait for to disappear' },
          selector: { type: 'string', description: 'CSS selector to wait for' },
          state: {
            type: 'string',
            enum: ['visible', 'hidden', 'attached', 'detached'],
            description: 'State to wait for (visible, hidden, attached, detached)',
          },
          timeout: { type: 'number', description: 'Maximum wait time in milliseconds' },
        },
      },
    },
    {
      name: 'browser_close',
      description: 'Close the current page',
      inputSchema: {
        type: 'object',
        properties: {},
      },
    },
    {
      name: 'browser_resize',
      description: 'Resize the browser viewport',
      inputSchema: {
        type: 'object',
        properties: {
          width: { type: 'number', description: 'Viewport width in pixels' },
          height: { type: 'number', description: 'Viewport height in pixels' },
        },
        required: ['width', 'height'],
      },
    },
    {
      name: 'browser_tabs',
      description: 'List, create, close, or select a browser tab',
      inputSchema: {
        type: 'object',
        properties: {
          action: {
            type: 'string',
            enum: ['list', 'new', 'close', 'select'],
            description: 'Operation to perform',
          },
          index: {
            type: 'number',
            description: 'Tab index for close/select operations',
          },
        },
        required: ['action'],
      },
    },
    {
      name: 'browser_list_downloads',
      description: 'List all files downloaded during this browser session.',
      inputSchema: {
        type: 'object',
        properties: {},
      },
    },
    {
      name: 'browser_read_download',
      description:
        'Read the content of a downloaded file. For PDFs, extracts text. ' +
        'For text formats, reads as text. Binary files return metadata only.',
      inputSchema: {
        type: 'object',
        properties: {
          downloadId: {
            type: 'number',
            description: 'ID of the download to read. Defaults to most recent.',
          },
        },
      },
    },
    {
      name: 'browser_pdf_read',
      description:
        "Fetch a PDF from an http(s) URL and extract its text content using the browser's authenticated session. For downloaded file: URLs, use browser_list_downloads then browser_read_download instead.",
      inputSchema: {
        type: 'object',
        properties: {
          url: {
            type: 'string',
            description:
              'HTTP(S) PDF URL or browser-relative path to fetch and extract text from. Do not pass file: URLs; use browser_read_download for captured files.',
          },
        },
        required: ['url'],
      },
    },
    {
      name: 'browser_hint',
      description:
        'Leave a persistent hint on a DOM element to help future agents navigate this page. ' +
        'Hints describe what elements ARE and DO — they are not workflow instructions.\n\n' +
        'WHEN TO LEAVE HINTS:\n' +
        '- You discovered what an ambiguous element does (icon-only buttons, unlabeled controls)\n' +
        '- You learned a format requirement the hard way (date formats, required fields, character limits)\n' +
        '- You noticed a pattern that applies to a class of elements ("all X require Y")\n' +
        '- You gained domain knowledge about how a page works that isn\'t obvious from the UI\n\n' +
        'WHAT MAKES A GOOD HINT:\n' +
        '- Names the specific element using real terms: "the gear icon", "the Export button"\n' +
        '- Describes behavior, not instructions: "opens modal with name/tags/status fields" not "click this to edit"\n' +
        '- Includes non-obvious details: hidden fields, autosave behavior, required formats\n' +
        '- For patterns: states the rule and which elements it applies to\n\n' +
        'CATEGORIES:\n' +
        '- element: what a specific element does (gear icon → modal with name, tags, status)\n' +
        '- format: input requirements (date picker → MM/DD/YYYY, phone field → +1 format)\n' +
        '- pattern: rules for a class of elements (all login nodes need valid credentials)\n' +
        '- guidance: page-level domain knowledge (good workflows start with login then 1-2 actions)\n\n' +
        'To INVALIDATE an outdated hint, pass invalidate=true with a reason.',
      inputSchema: {
        type: 'object',
        properties: {
          ref: {
            type: 'string',
            description: 'Element reference from snapshot (e.g., "e42")',
          },
          category: {
            type: 'string',
            enum: ['element', 'format', 'pattern', 'guidance'],
            description: 'Hint category — determines scope and rendering',
          },
          element: {
            type: 'string',
            description:
              'Description of the specific element being annotated. Must use real terms — ' +
              '"the gear icon", "the Save button", "the date picker in the Scheduling section". ' +
              'Never use pronouns or refs.',
          },
          frontmatter: {
            type: 'string',
            description:
              'Compact annotation (max 120 chars) — what the element does or reveals. ' +
              'Describe behavior, not instructions.',
          },
          body: {
            type: 'string',
            description:
              'Optional extended documentation about this element\'s behavior. ' +
              'Can describe edge cases, hidden states, interaction patterns.',
          },
          invalidate: {
            type: 'boolean',
            description: 'Set to true to mark the hint on this element as no longer relevant.',
          },
          reason: {
            type: 'string',
            description: 'Required when invalidate=true — why this hint is outdated.',
          },
        },
        required: ['ref'],
      },
    },
  ];
}

/** Names of tools that are lifecycle-only (handled by CLI, not executor) */
export const BROWSER_LIFECYCLE_TOOLS = ['browser_start', 'browser_stop'] as const;

/** Get tool definitions including lifecycle tools for CLI MCP registration */
export function getBrowserToolDefinitionsWithLifecycle(): BrowserToolDefinition[] {
  return [
    {
      name: 'browser_start',
      description:
        'Launch a local browser for direct automation. Returns when browser is ready. ' +
        'The browser opens in headed mode so you can watch it.',
      inputSchema: {
        type: 'object',
        properties: {
          headless: {
            type: 'boolean',
            description: 'Run browser headless (default: false — headed so you can watch)',
          },
          storageStatePath: {
            type: 'string',
            description: 'Path to Playwright storage state JSON for pre-authenticated sessions',
          },
        },
      },
    },
    {
      name: 'browser_stop',
      description: 'Stop the local browser and clean up resources.',
      inputSchema: {
        type: 'object',
        properties: {},
      },
    },
    ...getBrowserToolDefinitions(),
  ];
}
